/* eslint-disable */
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult, SortingOptions } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, SortingDirection, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { CUBE_ID } from "./constants";
import { DictionariesService } from "@sa/rep-mdm";
import { last } from "lodash";
import { clone } from "../../../../shared";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public olapCountriesProduction$ = new BehaviorSubject<OLAPResult | null>(null); // countries Production
  public olapCountriesArea$ = new BehaviorSubject<OLAPResult | null>(null); // cuntries Area
  public olapWorldProduction$ = new BehaviorSubject<OLAPResult | null>(null); // world Production
  public olapWorldArea$ = new BehaviorSubject<OLAPResult | null>(null); // world Area
  public olapCountriesYield$ = new BehaviorSubject<OLAPResult | null>(null); // yield
  public olapForecastCountriesYield$ = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);
  public orderedCountriesIds$ = new BehaviorSubject<number[]>([]);

  public highlightData = new BehaviorSubject<Record<string, string>>({});

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  }

  protected _commodities: BehaviorSubject<string[]>;
  public get commodities(): Observable<string[]> {
    return this._commodities.asObservable();
  }

  protected _units: BehaviorSubject<string[]>;
  public get units$(): Observable<string[]> {
    return this._units.asObservable();
  }

  protected _business: BehaviorSubject<number>;
  public get business(): Observable<number> {
    return this._business.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject(calendarParameter.value as string[]);
    const commoditiesParameter = createOptions.dataflowService.getParameter("@commodities") as MultiValueParameter;
    this._commodities = new BehaviorSubject(commoditiesParameter.value as string[]);
    const unitsParameter = createOptions.dataflowService.getParameter("@units") as MultiValueParameter;
    this._units = new BehaviorSubject(unitsParameter.value as string[]);
    const businessParameter = createOptions.dataflowService.getParameter("@business-units") as SingleValueParameter;
    this._business = new BehaviorSubject(businessParameter.value as number);

    combineLatest([this._calendar, this._commodities, this._business])
      .pipe(debounceTime(500))
      .subscribe(([calendar, commodities, business]) => {
        this.query({ calendar, commodities, business });
      });

    const exportParameters = [
      "@e_calendar", "@e_countries", "@e_yield_calendar"
    ];
    for (const eParamName of exportParameters) {
      let eParameter = this.options.dataflowService.getParameter(eParamName);
      if (eParameter === undefined) {
        this.options.dataflowService.parameters.add({
          id: eParamName,
          multiplicityType: MultiplicityType.Multiple,
          type: DataType.Number,
          allowNullable: false,
          value: []
        });
        eParameter = this.options.dataflowService.getParameter(eParamName);
      }
      this._exportParameters.set(eParamName, eParameter!);
    }
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.dataflowIntegration.integrated = true;
      const cube = await dataService.getCubeDefinition(CUBE_ID);
      this.cube.next(cube);
    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {
      this._calendar.next(calendar as string[]);
    }
    const commodities = command.values.get("@commodities");
    if (commodities !== undefined) {
      this._commodities.next(commodities as string[]);
    }
    const units = command.values.get("@units");
    if (units !== undefined) {
      this._units.next(units as string[]);
    }
    const business = command.values.get("@business-units");
    if (business !== undefined) {
      this._commodities.next([]);
      this._business.next(business as number);
    }
  }

  async query(parameters: { calendar: string[]; commodities: string[]; business: number }) {
    const { calendar, commodities, business } = parameters;
    const calendarId = Number(calendar[0]);
    if (calendarId && commodities?.length) {
      this.readyState = ComponentContextReadyState.Loading;
      this.olapCountriesProduction$.next(null);
      this.olapCountriesArea$.next(null);
      this.olapWorldProduction$.next(null);
      this.olapWorldArea$.next(null);
      this.olapCountriesYield$.next(null);


      const { dataSourcesService } = this.options;
      const commodityId = Number(commodities[0]);
      const sources = await dataSourcesService.getDataSources(6, business, commodityId);
      this.sources$.next(sources);


      const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;
      const dictSlideCountries = await dictionaryService.getDictionary({
        id: 'slides-countries-display',
        parameters: {
          'slide': 6,
          'olap_commodity': Number(commodities[0]),
          'business_unit': business
        }
      });
      const itemsCountriesRelationship = new Map((await dictSlideCountries.getAll()).map(x => [Number(x.getValue("olap_country")), Number(x.id)]));
      const itemsSlideCountries = Array.from(itemsCountriesRelationship.keys());
      
      const calendarAttempts = [Number(calendarId) + 100000, Number(calendarId)]; // Прогнозируемый, если нет, то текущий
      let itemsTopCountries: number[] = [];
      for (const calendarAttempt of calendarAttempts) {
        const dictTopCountries = await dictionaryService.getDictionary({
          id: 'dict_f_lead_production_countries',
          parameters: {
            'p_commodity': Number(commodities[0]),
            'p_calendar': calendarAttempt,
            'p_global_unit': business,
            'p_slide': 6,
        }
        });
        itemsTopCountries = (await dictTopCountries.getAll()).map(x => Number(x.id));
        if (itemsTopCountries.length) {
          break;
        }
      }

      const itemsCountries = itemsTopCountries.filter(id => itemsSlideCountries.includes(id));
      let countries = itemsCountries.slice(0, 15);
      if (countries.length === 0) {
        countries = itemsTopCountries.slice(0, 15);
      }
      this.orderedCountriesIds$.next(countries);
      const worldCountry = business === 2 ? 20000 : 100231;
      const eCountriesParameter = this._exportParameters.get("@e_countries")! as MultiValueParameter;
      eCountriesParameter.value = countries.map(countryId => itemsCountriesRelationship.get(countryId));
      const eCalendarParameter = this._exportParameters.get("@e_calendar")! as MultiValueParameter;
      eCalendarParameter.value = [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000));
      const eCropCalendarParameter = this._exportParameters.get("@e_yield_calendar")! as MultiValueParameter;
      eCropCalendarParameter.value = [0, +10].map(x => calendarId + (x * 10000));

      const cubeSlideDef = await dataService.getCubeDefinition("slide1");
      const cubeParameters = clone(cubeSlideDef.parameters);
      cubeParameters["p_slide"]!["defaultValue"] = '6';
      cubeParameters["p_commodities"]!["defaultValue"] = commodities.join(',');
      cubeParameters["p_countries"]!["defaultValue"] = countries.concat(worldCountry).join(',');
      cubeParameters["p_attributes"]!["defaultValue"] = [1, 17, 22].join(',');
      cubeParameters["p_calendar"]!["defaultValue"] = [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)).join(',');
      cubeParameters["p_units"]!["defaultValue"] = [1, 7, 8, 9].join(',');
      cubeParameters["p_business"]!["defaultValue"] = business.toString();
      const cubeSlide = await dataService.getCube({
        id: "slide1",
        parameters: cubeParameters,
      });

      const olapResult$ = cubeSlide.query({
        columns: {
          showEmpty: false,
          parts: [
            {
              path: "countries.default",
              selection: countries.concat(worldCountry),
              selectionMode: SelectionMode.Include,
            },
          ],
        },
        filters: {
          showEmpty: false,
          parts: [
            { path: "units.default", selection: [1, 7, 8, 9], selectionMode: SelectionMode.Include },
            { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          ],
        },
        rows: {
          showEmpty: false,
          parts: [
            { path: "attributes.default", selection: [1, 17, 22], selectionMode: SelectionMode.Include },
            { path: "calendar.default", selection: [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)), selectionMode: SelectionMode.Include },
            { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }] },
          ],
        },
        sourceResolveOptions: {
          sourceId: cubeSlide.id,
        },
      } as any);

      let [olapResult] = await Promise.all([olapResult$]);

      const countriesResult = clone(olapResult);
      countriesResult.data.aggregatedSelection['countries.default'] = countriesResult.data.aggregatedSelection['countries.default'].filter(
        selection => Number(selection) !== worldCountry
      );
      countriesResult.data.rows.forEach(row => {
        row.cells = row.cells.filter(cell => Number(cell.selection['countries.default']) !== worldCountry);
      });

      const worldResult = clone(olapResult);
      worldResult.data.aggregatedSelection['countries.default'] = worldResult.data.aggregatedSelection['countries.default'].filter(
        selection => Number(selection) === worldCountry
      );
      worldResult.data.rows.forEach(row => {
        row.cells = row.cells.filter(cell => Number(cell.selection['countries.default']) === worldCountry);
      })

      const olapCountriesProduction = clone(countriesResult);
      const olapCountriesYield = clone(countriesResult);
      const olapForecastCountriesYield = clone(countriesResult);
      const olapCountriesArea = clone(countriesResult);
      const olapWorldProduction = clone(worldResult);
      const olapWorldArea = clone(worldResult);

      [
        [1, olapCountriesArea, olapWorldArea],
        [17, olapCountriesProduction, olapWorldProduction],
        [22, olapCountriesYield, olapForecastCountriesYield]
      ].forEach(args => {
        const [attributeId, ...olapAttrsData] = args;
        for (const olapAttrData of (olapAttrsData as OLAPResult[])) {
          delete olapAttrData.data.aggregatedSelection['attributes.default'];
          olapAttrData.data.rows = olapAttrData.data.rows.filter(
            row => {
              const isAttr = Number(row.selection['attributes.default']) === (attributeId as number);
              if (isAttr) {
                if (attributeId === 22) { 
                  switch (olapAttrData) {
                    case olapCountriesYield:
                      return [0].map(x => calendarId + (x * 10000)).includes(Number(row.selection['calendar.default']));
                    case olapForecastCountriesYield:
                      return [+10].map(x => calendarId + (x * 10000)).includes(Number(row.selection['calendar.default']));
                    default:
                      break;
                  }
                } else {
                  switch (olapAttrData) {
                    case olapWorldArea:
                    case olapWorldProduction:
                      return [-6, +10].map(x => calendarId + (x * 10000)).includes(Number(row.selection['calendar.default'])); 
                    default:
                      break;
                  }
                }
              }
              return isAttr;
            }
          );
        }
      })

      this.olapCountriesProduction$.next(olapCountriesProduction);
      this.olapCountriesYield$.next(olapCountriesYield);
      this.olapForecastCountriesYield$.next(olapForecastCountriesYield);
      this.olapCountriesArea$.next(olapCountriesArea);
      this.olapWorldProduction$.next(olapWorldProduction);
      this.readyState = ComponentContextReadyState.Loaded;
      this.olapWorldArea$.next(olapWorldArea);  
    }
  }
}
